import { Box, Group, Pagination, Table, Text, UnstyledButton } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconArrowsSort } from "@tabler/icons-react";
import {
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { EmptyState, ErrorState, LoadingState, PermissionDenied } from "./states";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- both generics are required by the base declaration
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Right-align the column and render it with tabular figures, so digits line up down the column. */
    readonly numeric?: boolean;
  }
}

export type DataTableStatus = "loading" | "error" | "denied" | "ready";
/** Row height: 40px standard, 36px for dense audit and log tables (docs/UI_STANDARDS.md, density system). */
export type DataTableDensity = "comfortable" | "dense";

export interface DataTableProps<TRow> {
  readonly columns: readonly ColumnDef<TRow>[];
  readonly rows: readonly TRow[];
  readonly status: DataTableStatus;
  /** Total rows on the server, for server-side pagination. */
  readonly totalRows: number;
  readonly pagination: PaginationState;
  readonly onPaginationChange: OnChangeFn<PaginationState>;
  readonly sorting: SortingState;
  readonly onSortingChange: OnChangeFn<SortingState>;
  readonly getRowId: (row: TRow) => string;
  readonly onRowClick?: (row: TRow) => void;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  readonly errorMessage?: string;
  readonly onRetry?: () => void;
  /** Accessible name for the table. */
  readonly label: string;
  readonly density?: DataTableDensity;
  /** Minimum width before the table scrolls inside its own container; the page never scrolls sideways. */
  readonly minWidth?: number;
}

/**
 * The one list-table pattern: TanStack Table (headless) rendered with Mantine Table, server-side sorting and pagination,
 * horizontal scrolling inside its own container, and the four async states built in.
 */
export function DataTable<TRow>({
  columns,
  rows,
  status,
  totalRows,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  getRowId,
  onRowClick,
  emptyTitle = "Nothing here yet",
  emptyDescription,
  errorMessage,
  onRetry,
  label,
  density = "comfortable",
  minWidth = 640,
}: DataTableProps<TRow>) {
  const table = useReactTable({
    data: rows as TRow[],
    columns: columns as ColumnDef<TRow>[],
    state: { pagination, sorting },
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    rowCount: totalRows,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
  });

  if (status === "loading") return <LoadingState rows={Math.min(pagination.pageSize, 8)} />;
  if (status === "denied") return <PermissionDenied />;
  if (status === "error") {
    return (
      <ErrorState
        {...(errorMessage === undefined ? {} : { message: errorMessage })}
        {...(onRetry === undefined ? {} : { onRetry })}
      />
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        {...(emptyDescription === undefined ? {} : { description: emptyDescription })}
      />
    );
  }

  const pageCount = Math.max(1, Math.ceil(totalRows / pagination.pageSize));
  return (
    <Box>
      {/* One cohesive framed panel: quiet outer border, subtle header, hairline row separators. */}
      <Box className="how-table-frame">
        {/* The scrollbar shows whenever the table is wider than its frame, not only on hover: a table cut off at
            the right with no scrollbar reads as clipped, which Groups' Status column was (2026-10-14). */}
        <Table.ScrollContainer minWidth={minWidth} scrollAreaProps={{ type: "auto" }}>
          <Table
            highlightOnHover={onRowClick !== undefined}
            aria-label={label}
            verticalSpacing={density === "dense" ? 7 : 9}
          >
            <Table.Thead>
              {table.getHeaderGroups().map((group) => (
                <Table.Tr key={group.id}>
                  {group.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const dir = header.column.getIsSorted();
                    const content = header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext());
                    return (
                      <Table.Th
                        key={header.id}
                        {...(header.column.columnDef.meta?.numeric === true
                          ? { className: "how-numeric" }
                          : {})}
                        aria-sort={
                          dir === "asc" ? "ascending" : dir === "desc" ? "descending" : undefined
                        }
                      >
                        {canSort ? (
                          <UnstyledButton
                            onClick={header.column.getToggleSortingHandler()}
                            aria-label={`Sort by ${header.column.id}`}
                          >
                            <Group gap={4} wrap="nowrap">
                              {content}
                              {dir === "asc" ? (
                                <IconArrowUp size={14} aria-hidden />
                              ) : dir === "desc" ? (
                                <IconArrowDown size={14} aria-hidden />
                              ) : (
                                <IconArrowsSort size={14} aria-hidden />
                              )}
                            </Group>
                          </UnstyledButton>
                        ) : (
                          content
                        )}
                      </Table.Th>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Thead>
            <Table.Tbody>
              {table.getRowModel().rows.map((row) => (
                <Table.Tr
                  key={row.id}
                  onClick={
                    onRowClick === undefined
                      ? undefined
                      : () => {
                          onRowClick(row.original);
                        }
                  }
                  style={onRowClick === undefined ? undefined : { cursor: "pointer" }}
                  tabIndex={onRowClick === undefined ? undefined : 0}
                  onKeyDown={
                    onRowClick === undefined
                      ? undefined
                      : (event) => {
                          if (event.key === "Enter") onRowClick(row.original);
                        }
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td
                      key={cell.id}
                      {...(cell.column.columnDef.meta?.numeric === true
                        ? { className: "how-numeric" }
                        : {})}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Box>
      <Group justify="space-between" mt="md" wrap="wrap" gap="sm">
        <Text size="sm" c="dimmed" className="how-numeric">
          {totalRows} total
        </Text>
        <Pagination
          total={pageCount}
          value={pagination.pageIndex + 1}
          onChange={(page) => {
            onPaginationChange({ ...pagination, pageIndex: page - 1 });
          }}
        />
      </Group>
    </Box>
  );
}
